import { Copy, Download, Sparkles } from "lucide-react"

import type { MissingTalentSummary } from "@/lib/talent-tracking-api"

import { Button } from "@/components/button"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { Card } from "@/components/ui/card"
import {
  useCopyMessage,
  type TalentOverviewUiStore,
} from "@/lib/stores/talent-overview-ui-store"

import { CATEGORY_LABELS, getMemberDisplayName } from "./constants"

type OverviewMetrics = {
  members: number
  bosses: number
  missing: number
}

export function TalentOverviewIntro({
  store,
  metrics,
  selectedSeason,
  selectedBoss,
  visibleTalents,
  isFetching,
  onRefresh,
}: {
  store: TalentOverviewUiStore
  metrics: OverviewMetrics
  selectedSeason: string
  selectedBoss: {
    id: string
    name: string
    season: string
    talents: MissingTalentSummary[]
  } | null
  visibleTalents: MissingTalentSummary[]
  isFetching: boolean
  onRefresh: () => void
}) {
  const copyMessage = useCopyMessage(store)

  const copyRaidPlan = async () => {
    if (!selectedBoss) {
      return
    }

    const lines = [
      `${selectedBoss.name} (${selectedBoss.season})`,
      ...visibleTalents.map(
        (talent) =>
          `- ${CATEGORY_LABELS[talent.category]} T${talent.tier}: ${
            talent.missingMembers
              .map((member) => getMemberDisplayName(member))
              .join(", ") || "No missing members"
          }`,
      ),
    ]

    await navigator.clipboard.writeText(lines.join("\n"))
    store.getState().setCopyMessage("Copied raid summary to clipboard")
  }

  const downloadRaidPlan = () => {
    if (!selectedBoss) {
      return
    }

    const body = [
      `${selectedBoss.name} (${selectedBoss.season})`,
      "",
      ...visibleTalents.map(
        (talent) =>
          `${CATEGORY_LABELS[talent.category]} T${talent.tier}\n${talent.description}\nMissing: ${
            talent.missingMembers
              .map((member) => getMemberDisplayName(member))
              .join(", ") || "No missing members"
          }`,
      ),
    ].join("\n\n")

    const blob = new Blob([body], { type: "text/plain;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `${selectedBoss.id}-raid-plan.txt`
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
    store.getState().setCopyMessage("Downloaded raid summary")
  }

  return (
    <Card className="page-intro border-primary/10">
      <PageIntroSplitHeader
        kicker={
          <SectionKicker icon={<Sparkles />}>Raid Readiness</SectionKicker>
        }
        title="See which players are still missing the exact boss talents needed for raid prep."
        titleClassName="max-w-4xl text-5xl leading-none font-semibold md:text-6xl"
        description="Use this board to spot bottlenecks quickly, group runs around missing mechanics, and plan targeted clears for the season."
        descriptionClassName="max-w-3xl text-base leading-7"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshButton isFetching={isFetching} onClick={onRefresh} />
            <Button
              type="button"
              variant="outline"
              onClick={() => void copyRaidPlan()}
            >
              <Copy className="size-4" />
              Copy Raid Summary
            </Button>
            <Button type="button" variant="outline" onClick={downloadRaidPlan}>
              <Download className="size-4" />
              Download Summary
            </Button>
            {copyMessage ? (
              <span className="text-sm text-success">{copyMessage}</span>
            ) : null}
          </div>
        }
        metrics={
          <>
            <MetricTile
              label="Tracked Members"
              value={String(metrics.members)}
              detail="Active guild members included in the overview."
            />
            <MetricTile
              label="Bosses"
              value={String(metrics.bosses)}
              detail={`Configured bosses in ${selectedSeason}.`}
            />
            <MetricTile
              label="Missing Slots"
              value={String(metrics.missing)}
              detail="Distinct player gaps across the selected season."
            />
          </>
        }
      />
    </Card>
  )
}
